<template>
<div data-module="contact-map">
  <div class="module-layout">

    <!-- container for all image content -->
    <div class="decoration-container"
      :class="containerClasses"
    >
      <div data-ornament="circle"/>
    </div>

    <!-- container for all image content -->
    <div class="map-container"
      :class="containerClasses"
    >
      <div class="image" :style="imageStyles" />
    </div>

    <!-- container for text content -->
    <div class="content-container"
      :class="containerClasses"
    >
      <markdown class="markdown">{{contentText}}</markdown>

      <div class="contact-container"
        v-if="!isMobile"
        :class="containerClasses"
      >
      <smart-address />
      <smart-phone />
      </div>
    </div>

    <!-- container for contact methods -->
    <div class="contact-container"
      v-if="isMobile"
      :class="containerClasses"
    >
      <smart-address />
      <smart-phone />
    </div>

  </div>
</div>
</template>

<script>
// Libs
import Vue from 'vue';

// Utils
import { getResponsiveUrl } from '@/utils/image-utils';
import ResponsiveMixin from '@/mixins/responsive-mixin';

// Components
import SmartAddress from '@/components/SmartAddress';
import SmartPhone from '@/components/SmartPhone';

export default {

  name: 'contact-map',

  mixins: [ResponsiveMixin],

  components: { SmartAddress, SmartPhone },

  props: ['data'],

  data() {
    return {
      imageContentHeight: 0,
      imageContentWidth: 0,
    };
  },

  computed: {

    contentText() {
      const { content } = this.data;
      return content;
    },

    containerClasses() {
      // return class string, eg 'desktop', 'mobile.reversed'
      const { deviceType } = this;
      const classes = [deviceType];
      return classes.join(' ');
    },

    imageStyles() {
      const { imageContentHeight, imageContentWidth } = this;
      const { image } = this.data;
      const { hAlign: hA, vAlign: vA } = image;

      // add defaults
      const hAlign = hA || 'center';
      const vAlign = vA || 'center';

      const maxDim = Math.max(imageContentWidth, imageContentHeight);
      const rUrl = getResponsiveUrl(image.src, maxDim);
      const styles = {
        backgroundImage: `url(${rUrl})`,
        backgroundPosition: `${hAlign} ${vAlign}`,
      };

      return styles;
    },

  },

  methods: {

    measureLayout() {
      this.imageContentHeight = this.getImageContentHeight();
      this.imageContentWidth = this.getImageContentWidth();
    },

    getImageContentHeight() {
      const el = this.$refs.imageContainer;
      return (el) ? el.clientHeight : 0;
    },

    getImageContentWidth() {
      const el = this.$refs.imageContainer;
      return (el) ? el.clientWidth : 0;
    },

  },

  watch: {

    currentWidth(/* newVal, oldVal */) {
      Vue.nextTick(() => {
        this.measureLayout();
      });
    },

  },

  mounted() {
    this.measureLayout();
  },

};
</script>


<style lang="scss" scoped>
@import '~@/styles/_mixins.scss';
@import '~@/styles/_grid.scss';

[data-module="contact-map"] {
  width: 100%;

  // media query props
  margin-bottom: rem($layout--default-module-spacing-mobile);

  @include ngMq($nbp--tablet, up) {
    margin-bottom: rem($layout--default-module-spacing-tablet);
  }

  @include ngMq($nbp--desktop, up) {
    margin-bottom: rem($layout--default-module-spacing-desktop);
  }

  .module-layout {
    // media query props
    @include ngGrid('mobile');

    @include ngMq($nbp--tablet, up) {
      @include ngGrid('tablet');
    }

    @include ngMq($nbp--desktop, up) {
      @include ngGrid('desktop');
    }

    /*
      decoration container
    */

    .decoration-container {

      [data-ornament] {

        // media query props
        width: rem(60px);
        margin-top: rem(46 - $layout--default-module-spacing-mobile);

        @include ngMq($nbp--tablet, up) {
          width: rem(60px);
          margin-top: rem(34 - $layout--default-module-spacing-tablet);
        }
        @include ngMq($nbp--desktop, up) {
          width: rem(90px);
          margin-top: 0 // rem(-10px);
        }
      }
    }

    .decoration-container.mobile {
      grid-column: 1 / -1;
      grid-row: 1;
      margin-bottom: rem((20 - $grid-gap-mobile) * 1px);
    }

    .decoration-container.tablet {
      grid-column: 7 / -1;
      grid-row: 1;
      margin-bottom: rem((56 - $grid-gap-tablet) * 1px);
    }

    .decoration-container.desktop {
      grid-column: 8 / -1;
      grid-row: 1;
      margin-bottom: rem((80 - $grid-gap-desktop) * 1px);
    }


    /*
      content container
    */

    .content-container {
      text-align: left;
    }

    .content-container.mobile {
      grid-column: 1 / -1;
      grid-row: 2;
      margin-top: rem(-2px); // offset font line-height
      margin-bottom: rem((36 - $grid-gap-mobile) * 1px);
    }

    .content-container.tablet {
      grid-column: 7 / span 6;
      grid-row: 2;
      margin-top: rem(-4px); // offset font line-height
      margin-bottom: rem((56 - $grid-gap-tablet) * 1px);
    }

    .content-container.desktop {
      grid-column: 8 / span 4;
      grid-row: 2;
      margin-top: rem(-4px); // offset font line-height
      margin-bottom: rem((56 - $grid-gap-desktop) * 1px);
    }

    /*
      image container, general
    */

    .map-container {

      .image {
        /*
          on image style attrs, above
          background-image
          background-position
        */
        overflow: hidden;
        background-size: cover;
        background-repeat: no-repeat;

        width: 100%;
      }
    }

    .map-container.mobile {
      grid-column: 1 / -1;
      grid-row: 3;
      margin: 0 rem($grid-padding-mobile * -1px); // pull to edges
      margin-bottom: rem((36 - $grid-gap-mobile) * 1px);

      display: block;
    }
    .map-container.mobile .image {
      padding-bottom: $dimension-4-3;
    }

    .map-container.tablet {
      grid-column: 1 / span 6;
      grid-row: 2 / span 2;
      margin: 0;
    }
    .map-container.tablet .image {
      padding-bottom: $dimension-4-3;
    }

    .map-container.desktop {
      grid-column: 1 / span 6;
      grid-row: 2 / span 2;
      margin: 0;
      margin-right: rem($grid-gap-desktop * -1px);
    }

    .map-container.desktop .image {
      padding-bottom: $dimension-4-3;
    }

    /*
      contact methods container
    */

    .contact-container {
      margin: 0;
    }

    .contact-container.mobile {
      grid-column: 1 / -1;
      grid-row: 4;
      margin-top: rem(-4px); // offset font line-height
      margin-right: 0;
    }

    .contact-container.tablet {
      margin-top: rem(50px - 10px); // offset font line-height
    }

    .contact-container.desktop {
      margin-top: rem(50px - 10px); // offset font line-height
    }

  }

}
</style>


<style lang="scss">
@import '~@/styles/_typography.scss';

[data-module="contact-map"] {

  .markdown {

    h2 {
      // media query props
      @include serif( $size: (35, 35, 0), $weight: 'bold');
      margin-bottom: rem(12px);

      @include ngMq($nbp--tablet, up) {
        @include serif( $size: (50, 50, 0), $weight: 'bold');
      }
      @include ngMq($nbp--desktop, up) {
        @include serif( $size: (72, 70, 0), $weight: 'bold');
      }
    }

    p:last-child {
      // remove last margin – simplifies achieving design layout
      margin-bottom: 0;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/contact/ContactMap.vue